import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, ShieldCheck, Search, ScanLine, QrCode,
  Fingerprint, Lock, Sparkles, Building2, Globe2, Calendar, Hash,
  ArrowRight, Loader2, AlertCircle,
} from "lucide-react";
import { api } from "../lib/api";
import client from "../lib/api";

/**
 * Premium Executive Verification Portal — public, no login required.
 * Supports:
 *   • Document verification via reference (e.g. E360-A1B2C3D4)
 *   • Digital Student Passport verification (e.g. PASS-xxxxxx)
 * Designed to match the Executive Intelligence Centre design language.
 */
export default function VerifyPortal() {
  const params = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(params.verificationId || params.passportId || "");
  const [mode, setMode] = useState("auto"); // auto | passport | document
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (params.passportId) verifyPassport(params.passportId);
    else if (params.verificationId) verifyDoc(params.verificationId);
  }, [params.passportId, params.verificationId]);

  const verifyDoc = async (rid) => {
    setLoading(true); setResult(null); setError(null);
    try {
      const { data } = await api.verify(rid);
      setResult({ kind: "document", ...data });
    } catch {
      setError("Verification service unavailable. Please try again shortly.");
    }
    setLoading(false);
  };

  const verifyPassport = async (pid) => {
    setLoading(true); setResult(null); setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/passport/verify/${pid}`);
      const data = await res.json();
      setResult({ kind: "passport", ...data });
    } catch {
      setError("Verification service unavailable. Please try again shortly.");
    }
    setLoading(false);
  };

  const detect = (v) => {
    const s = (v || "").trim().toUpperCase();
    if (s.startsWith("PASS-")) return "passport";
    return "document";
  };

  const submit = (e) => {
    e.preventDefault();
    if (!ref) return;
    const kind = mode === "auto" ? detect(ref) : mode;
    if (kind === "passport") {
      navigate(`/education360/verify/passport/${ref.trim()}`);
      verifyPassport(ref.trim());
    } else {
      navigate(`/education360/verify/${ref.trim()}`);
      verifyDoc(ref.trim());
    }
  };

  return (
    <div className="edu360-root min-h-screen text-slate-800" data-testid="verify-portal"
         style={{ background: "radial-gradient(circle at 15% 0%, #001432 0%, #000a1c 55%, #04060d 100%)" }}>
      {/* Ambient particles */}
      <div className="fixed inset-0 pointer-events-none opacity-40 z-0" aria-hidden>
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="absolute rounded-full bg-white/10"
            style={{
              width: `${2 + (i % 5)}px`, height: `${2 + (i % 5)}px`,
              left: `${(i * 71) % 100}%`, top: `${(i * 37) % 100}%`,
              animation: `edu-float ${16 + i}s ease-in-out ${i * 0.7}s infinite`,
            }} />
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-white/[0.02] backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-md grid place-items-center font-display font-bold text-sm text-white shadow-xl"
              style={{ background: "linear-gradient(135deg, #002147 0%, #002147 50%, #D9531E 50%, #D9531E 100%)" }}
              data-testid="verify-crest"
            >E3</div>
            <div>
              <div className="font-display font-bold text-white text-lg leading-tight">Education360™</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mt-0.5">Global Document Verification</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-[10px] uppercase tracking-[0.28em] text-white/40">
            <span className="flex items-center gap-1.5"><Globe2 className="w-3 h-3 text-[color:var(--hl-orange)]" /> International</span>
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-[color:var(--hl-orange)]" /> Signed</span>
            <span className="flex items-center gap-1.5"><Fingerprint className="w-3 h-3 text-[color:var(--hl-orange)]" /> Tamper-Proof</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-10">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] mb-4">
              <ShieldCheck className="w-3.5 h-3.5" /> Public verification · No login required
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white tracking-tight leading-[1.05]">
              Verify any Education360<span className="text-[color:var(--hl-orange)]">™</span> credential in seconds.
            </h1>
            <p className="text-white/70 text-base mt-5 max-w-xl leading-relaxed">
              Ministries of education, accreditation bodies, universities, employers and inspectors can instantly verify the authenticity of any report card, transcript, certificate or Digital Student Passport issued by an Education360 partner school.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-8 max-w-xl">
              <TrustPill icon={Lock} label="Cryptographically signed" />
              <TrustPill icon={Fingerprint} label="SHA-256 sealed" />
              <TrustPill icon={ScanLine} label="QR-scanable" />
            </div>
          </div>

          {/* Verify card */}
          <div className="lg:col-span-2">
            <form onSubmit={submit}
              className="rounded-xl p-6 border border-white/10 shadow-2xl backdrop-blur-xl relative overflow-hidden"
              style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)" }}
              data-testid="verify-form">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-[color:var(--hl-orange)] to-transparent" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold flex items-center gap-1.5">
                <QrCode className="w-3 h-3" /> Instant verification
              </div>
              <h2 className="font-display font-bold text-white text-xl mt-1 mb-4">Enter a reference number</h2>

              <div className="flex gap-1 mb-3 p-1 rounded-md bg-white/5 border border-white/10 text-[10px]">
                {["auto", "document", "passport"].map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)}
                    data-testid={`mode-${m}`}
                    className={`flex-1 px-2 py-1.5 rounded uppercase tracking-widest font-semibold transition ${
                      mode === m ? "bg-[color:var(--hl-orange)] text-white" : "text-white/60 hover:text-white"
                    }`}>{m === "auto" ? "Auto-detect" : m}</button>
                ))}
              </div>

              <div className="relative">
                <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="E360-A1B2C3D4 or PASS-9F3B2A6C1D0E"
                  className="w-full pl-9 pr-3 py-3 text-sm bg-white/5 border border-white/10 rounded-md text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[color:var(--hl-orange)] focus:border-transparent"
                  data-testid="verify-input"
                />
              </div>

              <button type="submit" disabled={!ref || loading}
                data-testid="verify-submit"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-md font-semibold text-sm text-white shadow-lg transition disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #D9531E 0%, #b8391a 100%)" }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {loading ? "Verifying…" : "Verify credential"}
              </button>

              <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mt-4 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" /> No confidential content is exposed
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Result */}
      {(error || result) && (
        <section className="relative z-10 max-w-4xl mx-auto px-6 pb-16">
          {error && (
            <div className="rounded-lg p-5 border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm" data-testid="verify-error">
              {error}
            </div>
          )}
          {result && <ResultCard result={result} />}
        </section>
      )}

      {/* Trust bar */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-4">
          <TrustBlock icon={Building2} title="Trusted by international schools"
            body="Every certified partner school issues credentials with a cryptographic signature bound to Education360's central authority." />
          <TrustBlock icon={Sparkles} title="Central Metrics Service backed"
            body="Every published credential is generated from the school's single source of truth — the Central Metrics Service — so a verified pass is a verified fact." />
          <TrustBlock icon={ShieldCheck} title="Advisory & tamper-detected"
            body="If a document has been altered after publication, this portal will flag it. Revoked or superseded credentials are surfaced automatically." />
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-[0.28em] text-white/40">
          <span>© {new Date().getFullYear()} Education360™ · Global Verification Portal</span>
          <Link to="/education360" className="text-white/70 hover:text-white flex items-center gap-1">
            Return to Education360 <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </footer>
    </div>
  );
}

function TrustPill({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 bg-white/5 text-[10px] uppercase tracking-[0.2em] text-white/70">
      <Icon className="w-3.5 h-3.5 text-[color:var(--hl-orange)] flex-shrink-0" /> {label}
    </div>
  );
}

function TrustBlock({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl p-6 border border-white/10 bg-white/[0.03] backdrop-blur">
      <Icon className="w-6 h-6 text-[color:var(--hl-orange)] mb-3" />
      <div className="font-display font-bold text-white text-base tracking-tight mb-1.5">{title}</div>
      <div className="text-xs text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}

function ResultCard({ result }) {
  const authentic = result.authentic;
  const bgColor = authentic ? "from-emerald-500/20 to-emerald-500/5" : "from-rose-500/20 to-rose-500/5";
  const borderColor = authentic ? "border-emerald-400/40" : "border-rose-400/40";
  const icon = authentic ? <CheckCircle2 className="w-10 h-10 text-emerald-400 flex-shrink-0" /> : <XCircle className="w-10 h-10 text-rose-400 flex-shrink-0" />;
  const isPassport = result.kind === "passport";
  return (
    <div className={`rounded-xl border ${borderColor} p-6 shadow-2xl backdrop-blur-xl bg-gradient-to-br ${bgColor}`} data-testid="verify-result">
      <div className="flex items-start gap-4">
        {icon}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">Verification result</div>
          <div className={`font-display font-bold text-2xl sm:text-3xl tracking-tight mt-1 ${authentic ? "text-emerald-100" : "text-rose-100"}`}>
            {authentic
              ? (isPassport ? "Digital Student Passport authentic" : "Document is authentic")
              : (result.message || "Could not be verified")}
          </div>
          {authentic && (
            <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 pt-6 border-t border-white/10">
              {isPassport ? (
                <>
                  <MetaRow icon={Hash} label="Passport ID" value={<span className="mono">{result.passport_id}</span>} />
                  <MetaRow icon={Building2} label="School" value={result.school?.name} />
                  <MetaRow icon={Fingerprint} label="Admission" value={result.student?.admission_no} />
                  <MetaRow icon={Globe2} label="Country" value={result.school?.country} />
                  <MetaRow icon={Calendar} label="Year group" value={result.student?.year_group} />
                  <MetaRow label="Initials" value={result.student?.initials} />
                  <MetaRow label="House" value={result.student?.house} />
                  <MetaRow label="Status" value={<span className="capitalize">{result.student?.status}</span>} />
                </>
              ) : (
                <>
                  <MetaRow icon={Hash} label="Reference" value={<span className="mono">{result.verification_id}</span>} />
                  <MetaRow icon={Building2} label="School" value={result.school?.name} />
                  <MetaRow label="Document" value={<span className="capitalize">{(result.document_type || "").replace(/_/g, " ")}</span>} />
                  <MetaRow label="Label" value={result.document_label} />
                  <MetaRow label="Version" value={`v${result.version}`} />
                  <MetaRow label="Period" value={`${result.period || ""} · ${result.academic_year || ""}`} />
                  <MetaRow icon={Calendar} label="Issued" value={(result.issued_on || "").slice(0, 10)} />
                  <MetaRow icon={Lock} label="Signature" value={<span className="capitalize text-emerald-300 font-semibold">{result.signature_status}</span>} />
                  {result.sha256 && <MetaRow icon={Fingerprint} label="SHA-256" value={<span className="mono text-[10px] break-all">{result.sha256.slice(0, 40)}…</span>} />}
                </>
              )}
              <MetaRow label="Issue authority" value={result.issue_authority || "Education360™"} />
              <MetaRow label="Verified at" value={new Date(result.verified_at || Date.now()).toLocaleString()} />
            </div>
          )}
          {authentic && result.status && result.status !== "valid" && (
            <div className="mt-4 text-xs text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-md px-3 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> Document status: <span className="uppercase font-semibold">{result.status}</span>
              {result.superseded_by_versions > 0 && <span>· {result.superseded_by_versions} newer version(s) exist</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-sm font-semibold text-white/90 mt-1 break-words">{value || "—"}</div>
    </div>
  );
}
